import { EVENT_LABEL_KEYS } from '../presentation-catalog.js';
// SCR-03 usage-record list and SCR-04 record detail (plan 8.4.3, 8.4.4, 7.5). Lists are filtered per record by
// logs.list (team scope = team at the time of the record, excluding other managers/admins/content reviewers).
// Derived text (first-question title) is only ever returned to the record's owner (plan 7.5.4).
import { z } from 'zod';
import {
  Channel,
  CostKind,
  DisplayState,
  Effort,
  ErrorType,
  FileKind,
  Method,
  MissingReason,
  PartialReason,
  ReasonCategory,
  ReasonDetail,
  RecordBadge,
  RecordLevel,
  Surface,
  SurfaceFilter,
  SupportItem,
  Tool,
  VersionState,
  Via,
  ToolGroup,
  DecisionActor,
  DecisionSource,
  ToolDecision,
} from '../enums.js';
import { RecordTab } from '../roles-menus.js';
import {
  CardFooter,
  Count,
  EpochMs,
  Id,
  ModelFilter,
  PageMeta,
  PersonRef,
  RecordRef,
  RoleRef,
  TeamRef,
  ToolFilter,
  ValuedInt,
  ValuedNumber,
  AppliedFilter,
  ExportRequest,
  filterQuery,
  valued,
  PageQuery,
  Ymd,
} from '../common.js';

/** Path params: `ref` = record id or display number ("1330" or "#1330"). */
export const RecordRefParams = z.object({ ref: z.string().regex(/^#?[A-Za-z0-9_-]{1,80}$/) });

/** GET /api/v1/records — common filter + tool/model/surface chips + search + page (15 rows). */
export const RecordListQuery = filterQuery({
  tool: ToolFilter.optional(),
  model: ModelFilter.optional(),
  surface: SurfaceFilter.optional(),
  /** Own records: question text, file names, number. Others: number only (plan 8.4.3, DC-07). */
  q: z.string().trim().max(100).optional(),
  ...PageQuery.shape,
});
export type RecordListQuery = z.infer<typeof RecordListQuery>;

/** Sub-agent or helper conversation (D-445): 'subagent' the AI started for part of the work, 'helper' the CLI's own request (task title). */
export const AgentRole = z.enum(['subagent', 'helper']);
/** 'source': the CLI named the parent conversation; 'estimated': matched by device, time and the helper's fixed instruction. */
export const LinkBasis = z.enum(['source', 'estimated']);
/** An agent inside one record: Claude subagent id with its type (general-purpose), or a Codex agent path tail. */
export const AgentRef = z.object({ id: z.string().max(80), name: z.string().max(120).nullable() });
export type AgentRef = z.infer<typeof AgentRef>;
export const LinkedRecord = z.object({
  record: RecordRef,
  role: AgentRole,
  name: z.string().max(120).nullable(),
  basis: LinkBasis,
  startedAt: EpochMs,
  endedAt: EpochMs,
  model: z.string().nullable(),
  prompts: Count,
  actions: Count,
  tokens: ValuedNumber,
});
export type LinkedRecord = z.infer<typeof LinkedRecord>;

export const RecordRow = z.object({
  record: RecordRef,
  startedAt: EpochMs,
  person: PersonRef,
  /** Team at the time of the record. */
  team: TeamRef.nullable(),
  tool: Tool,
  method: Method,
  surface: Surface.nullable(),
  mainModel: z.string().nullable(),
  /** First-question summary — only for the owner while question text is available; else null → "대화 #no · 질문 n개 · n자". */
  title: z.string().nullable(),
  /** Reserved owner-only project label. P0–P5 return null: identifying workspace/vcs attributes are dropped (plan 5.7, 11.7; Q-20/D-400). */
  project: z.string().max(120).nullable(),
  prompts: Count,
  chars: Count,
  /** AI actions (tool runs); API calls → none · not_applicable. */
  actions: ValuedInt,
  filesEdited: ValuedInt,
  tokens: ValuedNumber,
  /** 대화가 열려 있던 시간 (ms); API calls → none · not_applicable. */
  openMs: ValuedNumber,
  recordLevel: RecordLevel,
  badges: z.array(RecordBadge),
  /** Owner only: how many times others opened this record (null for others). */
  viewedCount: Count.nullable(),
  /** Sub-agent and helper conversations shown inside this one (D-445); their usage is in tokens/actions. Absent when none. */
  linked: z.object({ subagents: Count, helpers: Count }).optional(),
});
export type RecordRow = z.infer<typeof RecordRow>;

/** "'Codex' 조건을 빼면 18건 있어요" — counts without one active condition (top 3, count > 0). */
export const RelaxSuggestion = z.object({
  drop: z.enum(['method', 'tool', 'model', 'surface', 'team', 'user', 'q', 'period']),
  value: z.string().nullable(),
  /** For drop = period: the wider preset that would match. */
  period: z.enum(['90d']).nullable(),
  count: Count,
});

export const RecordListResponse = z.object({
  applied: AppliedFilter,
  items: z.array(RecordRow),
  page: PageMeta,
  /** Counts per chip with the other active conditions applied (exclude-self facets). */
  facets: z.object({
    method: z.partialRecord(Method, Count),
    tool: z.partialRecord(Tool, Count),
    model: z.array(z.object({ id: z.string(), label: z.string(), count: Count })),
    surface: z.partialRecord(SurfaceFilter, Count),
    /** Only when the list scope is wider than 'me'; name order, never value order. */
    person: z.array(z.object({ person: PersonRef, count: Count })).nullable(),
  }),
  suggestions: z.array(RelaxSuggestion),
  totals: z.object({ prompts: Count, tokens: ValuedNumber, viaGatewayCalls: Count }),
  footer: CardFooter,
  /** No connection and no record ever → the 'not connected yet' empty state. */
  notConnectedYet: z.boolean(),
  /** logs.list scope narrower than the header scope (e.g. stats viewer sees only own records). */
  narrowedToSelf: z.boolean(),
  canExport: z.boolean(),
  /** Rows of this filter inside the viewer's export.rows scope ("n건 중 m건만 담겨요"); null when canExport is false. */
  exportableRows: Count.nullable(),
});
export type RecordListResponse = z.infer<typeof RecordListResponse>;

/** Lock/availability of one text part of a record (prompt, answer, files, diff, commands, tool output). */
export const CONTENT_ACCESS_STATES = ['open', 'needs_grant', 'locked', 'none'] as const;
export const ContentAccess = z.object({
  /** open: readable now · needs_grant: stored, fixed flow can start · locked: no permission · none: nothing to show. */
  state: z.enum(CONTENT_ACCESS_STATES),
  reason: MissingReason.optional(),
  detail: ReasonDetail.optional(),
  partial: PartialReason.optional(),
  /** Stored text length for the lock box ("글은 저장되어 있어요 · 1,240자"). */
  chars: Count.optional(),
  /** Count-only facts for locked work detail (no paths, no hunks). */
  counts: z.object({ files: Count, changedFiles: Count, added: Count, removed: Count }).partial().optional(),
});
export type ContentAccess = z.infer<typeof ContentAccess>;

/** "열람: 관리자(사용자 코드 A7) · 2026-10-20 14:05" — repeated over opened text (plan 7.5.1 ⑤). */
export const Watermark = z.object({ role: RoleRef, viewerCode: z.string().min(2).max(8), at: EpochMs });
export type Watermark = z.infer<typeof Watermark>;

export const TabState = z.object({
  state: z.enum(['open', 'locked', 'dimmed']),
  reason: MissingReason.optional(),
  detail: ReasonDetail.optional(),
});
export const ValuedSurface = valued(Surface);
export const ValuedEffort = valued(Effort);

/**
 * A record blocked by a deletion request (plan 8.4.4 "삭제 요청으로 볼 수 없어요", 11.9): every route under
 * /api/v1/records/:ref answers 410 `record_deletion_requested` with these details — to the owner and to anyone whose
 * scope covers the record (out of scope stays 404). Rejected/canceled requests unblock the record.
 */
export const RecordDeletedDetails = z.object({
  record: RecordRef,
  /** DeletionStatus subset: blocked (waiting, 7 days at most) · held (legal hold) · deleted (text & identifiers gone). */
  status: z.enum(['blocked', 'held', 'deleted']),
  requestedAt: EpochMs,
  dueAt: EpochMs.nullable(),
  handledAt: EpochMs.nullable(),
  own: z.boolean(),
});
export type RecordDeletedDetails = z.infer<typeof RecordDeletedDetails>;

/** GET /api/v1/records/:ref — header strip, badges, tab states and lock facts. Out of scope → 404 (no existence leak). */
export const RecordDetailResponse = z.object({
  record: RecordRef,
  person: PersonRef,
  team: TeamRef.nullable(),
  tool: Tool,
  method: Method,
  channel: Channel,
  via: Via,
  surface: ValuedSurface,
  startedAt: EpochMs,
  endedAt: EpochMs,
  cliVersion: z.string().nullable(),
  versionState: VersionState.nullable(),
  mainModel: z.string().nullable(),
  otherModels: z.array(z.string()),
  effort: ValuedEffort,
  authMode: z.object({ value: z.enum(['subscription', 'api_key', 'unknown', 'not_applicable']), estimated: z.boolean(), checkedAt: EpochMs.nullable() }),
  tokens: ValuedNumber,
  openMs: ValuedNumber,
  activeMs: ValuedNumber,
  /** Counts only (no text): header "질문 n개 / 호출 n건" and the question tab's tech line, also while text is locked. */
  counts: z.object({ prompts: Count, slash: Count, answers: Count, requests: Count, failedRequests: Count }),
  recordLevel: RecordLevel,
  badges: z.array(RecordBadge),
  partialReasons: z.array(PartialReason),
  /** Hash-chain + anchor check of this record's ingest proof (null until P3). */
  integrity: z.object({ ok: z.boolean(), anchorDate: Ymd.nullable() }).nullable(),
  title: z.string().nullable(),
  tabs: z.record(RecordTab, TabState),
  defaultTab: RecordTab,
  content: z.object({
    prompt: ContentAccess,
    answer: ContentAccess,
    files: ContentAccess,
    diff: ContentAccess,
    commands: ContentAccess,
    toolOutput: ContentAccess,
  }),
  /** Someone else's record: current grant (or null) and my last denial. Own record: null. */
  access: z
    .object({
      active: z.boolean(),
      expiresAt: EpochMs.nullable(),
      watermark: Watermark.nullable(),
      lastDeniedAt: EpochMs.nullable(),
      canRequest: z.boolean(),
    })
    .nullable(),
  /** Owner only: "이 기록을 n번 열람했어요" with the latest view (role, time, reason category). */
  viewNotice: z.object({ count: Count, latest: z.object({ role: RoleRef, at: EpochMs, reasonCategory: ReasonCategory.nullable() }).nullable() }).nullable(),
  legalHold: z.boolean(),
  expired: z.boolean(),
  canRaw: z.boolean(),
  /** This record ran inside the user's conversation `parent` (D-445). */
  parent: z.object({ record: RecordRef, title: z.string().nullable(), role: AgentRole, name: z.string().max(120).nullable(), basis: LinkBasis }).nullable().optional(),
  /** Sub-agent and helper conversations of this record, oldest first (D-445). */
  linked: z.array(LinkedRecord).optional(),
});
export type RecordDetailResponse = z.infer<typeof RecordDetailResponse>;

const TokenCount = valued(Count);

/** Usage & time tab (plan 8.4.4). Four non-overlapping kinds; reasoning is inside output (not summed). */
export const RequestRow = z.object({
  index: z.int().positive(),
  at: EpochMs,
  model: z.string(),
  promptSeq: z.int().nullable(),
  background: z.boolean(),
  attempt: z.int().positive(),
  ok: z.boolean(),
  statusCode: z.int().nullable(),
  errorType: ErrorType.nullable(),
  refused: z.boolean(),
  tokens: z.object({ new: TokenCount, cacheRead: TokenCount, cacheWrite: TokenCount, output: TokenCount, reasoning: TokenCount, total: TokenCount }),
  ttftMs: z.number().nonnegative().nullable(),
  durationMs: z.number().nonnegative().nullable(),
  cost: z.object({ kind: CostKind, micros: z.int() }).nullable(),
});
export const RecordUsageResponse = z.object({
  /** Codex cache-write inclusion is unverified; OpenAI API cache-write is not applicable. Neither is stacked. */
  cacheWriteStacked: z.boolean(),
  tokens: z.object({ new: TokenCount, cacheRead: TokenCount, cacheWrite: TokenCount, output: TokenCount, reasoning: TokenCount, total: TokenCount }),
  /** One entry per cost kind present; kinds are never added together. */
  costs: z.array(z.object({ kind: CostKind, value: ValuedNumber })),
  activeMs: ValuedNumber,
  openMs: ValuedNumber,
  latency: z.object({ p50Ms: ValuedNumber, p95Ms: ValuedNumber, firstTokenP50Ms: ValuedNumber, n: Count, slowest: z.object({ index: z.int().positive(), ms: z.number() }).nullable() }),
  models: z.array(z.object({ model: z.string(), label: z.string(), requests: Count, background: Count, tokens: TokenCount })),
  effort: ValuedEffort,
  requests: z.array(RequestRow),
  failedRequests: Count,
});
export type RecordUsageResponse = z.infer<typeof RecordUsageResponse>;

/** Plain-language event label: i18n key + params (plan 9.4); the original event name travels as `rawName`. */
export const EventLabel = z.object({ key: z.enum(EVENT_LABEL_KEYS), params: z.record(z.string(), z.union([z.string(), z.number()])).optional() });
export type EventLabel = z.infer<typeof EventLabel>;

export const ACTION_CATEGORIES = ['tool', 'command', 'decision', 'error', 'system', 'access'] as const;
export const ActionCategory = z.enum(ACTION_CATEGORIES);
/** Allow/deny of a tool run: stored outcome (ToolDecision) and who decided (DecisionSource; null = not recorded → 'none'). */
export const ActionDecision = z.object({ outcome: ToolDecision, source: DecisionSource.nullable() });
export type ActionDecision = z.infer<typeof ActionDecision>;
/** "AI가 한 작업" tab — REQ-3.7 'other records' chips. Access rows only for the owner or audit.view holders. */
export const RecordActionsResponse = z.object({
  applicable: z.boolean(),
  rows: z.array(
    z.object({
      at: EpochMs,
      category: ActionCategory,
      label: EventLabel,
      rawName: z.string(),
      toolName: z.string().nullable(),
      group: ToolGroup.nullable(),
      /** Masked tool parameter / command; null with `detailState` when not visible (level, expiry, permission, flow). */
      detail: z.string().nullable(),
      detailState: z.object({ state: DisplayState, reason: MissingReason.optional() }).nullable(),
      success: z.boolean().nullable(),
      durationMs: z.number().nonnegative().nullable(),
      decision: ActionDecision.nullable(),
      /** Run by a subagent of this record (Claude, D-445); null for the main agent. */
      agent: AgentRef.nullable().optional(),
      access: z
        .object({ role: RoleRef, actor: PersonRef.nullable(), result: z.enum(['success', 'denied']), reasonCategory: ReasonCategory.nullable(), reason: z.string().nullable(), kind: z.enum(['content_view', 'export']) })
        .nullable(),
    }),
  ),
  counts: z.partialRecord(ActionCategory, Count),
  /** AI actions (tool runs) — the summary's "작업 n번". */
  actions: Count,
  commands: Count,
  rejects: Count,
  errors: Count,
  actors: z.partialRecord(DecisionActor, Count),
  /**
   * Access rows ("이 기록의 열람·내보내기") are shown to the owner and audit.view holders only. false → the chip is
   * disabled with "볼 수 없어요"; true with no access rows → "아직 아무도 열람하지 않았어요".
   */
  accessVisible: z.boolean(),
});
export type RecordActionsResponse = z.infer<typeof RecordActionsResponse>;

/** n is null when a snippet or a context-only patch header does not report an original file line number. */
export const DiffLine = z.object({ t: z.enum(['add', 'del', 'ctx']), n: z.int().nonnegative().nullable(), text: z.string() });
export const FileDiff = z.object({
  label: z.string(),
  path: z.string().nullable(),
  added: Count,
  removed: Count,
  truncated: z.boolean(),
  hunks: z.array(z.object({ header: z.string(), lines: z.array(DiffLine) })),
});
/**
 * Files & changes tab. `labels_only`: file labels (last two path segments, OS user folder removed) without full
 * paths/diffs — full paths & diffs need own record or an active grant (plan 7.2.2 note).
 */
export const RecordFilesResponse = z.object({
  state: z.enum(['open', 'labels_only', 'locked', 'none']),
  reason: MissingReason.optional(),
  detail: ReasonDetail.optional(),
  files: z.array(z.object({ label: z.string(), path: z.string().nullable(), ext: z.string(), kinds: z.array(FileKind), count: Count, estimated: z.boolean() })),
  counts: z.object({ read: Count, edited: Count }),
  diffs: z.array(FileDiff).nullable(),
  diffState: z.object({ state: DisplayState, reason: MissingReason.optional(), partial: PartialReason.optional() }),
  /** Claude: CLI metric (no per-file split). Codex: estimated from patches. */
  lines: z.object({ added: ValuedNumber, removed: ValuedNumber, source: z.enum(['metric', 'codex_patch', 'none']) }),
});
export type RecordFilesResponse = z.infer<typeof RecordFilesResponse>;

/** Display state of a text part (question/answer list, timeline snippets). */
export const TextState = z.object({ state: DisplayState, reason: MissingReason.optional(), partial: PartialReason.optional(), detail: ReasonDetail.optional(), tool: Tool.optional(), item: SupportItem.optional() });

export const TIMELINE_KINDS = ['prompt', 'request', 'tool', 'command', 'answer', 'error', 'decision', 'system'] as const;
/**
 * One step of the progress order (mockup logs.js turnItems). Text-bearing parts follow the content rules: `text`
 * (question/answer snippet ≤ 90 chars) and `detail` (file path or command) only for the owner or under an active
 * grant, else null with the matching state — the route is served no-store.
 */
export const TimelineStep = z.object({
  at: EpochMs,
  kind: z.enum(TIMELINE_KINDS),
  label: EventLabel,
  rawName: z.string(),
  durationMs: z.number().nonnegative().nullable(),
  ok: z.boolean().nullable(),
  /** prompt/answer: snippet when readable; `chars` always ("질문 1,240자"). */
  text: z.string().max(90).nullable().optional(),
  textState: TextState.optional(),
  chars: Count.optional(),
  /** tool/command: target file or masked command. */
  toolName: z.string().nullable().optional(),
  detail: z.string().nullable().optional(),
  detailState: z.object({ state: DisplayState, reason: MissingReason.optional() }).optional(),
  decision: ActionDecision.nullable().optional(),
  /** request/error steps: which call, model, usage, wait and first-token times, background call, failure kind. */
  requestIndex: z.int().positive().optional(),
  model: z.string().optional(),
  tokens: ValuedNumber.optional(),
  ttftMs: z.number().nonnegative().nullable().optional(),
  background: z.boolean().optional(),
  errorType: ErrorType.nullable().optional(),
});
/** Progress order: question → AI call → tool run/allow/deny → result → answer, grouped by turn (CLI) or call (API). */
export const RecordTimelineResponse = z.object({
  kind: z.enum(['cli', 'api']),
  turns: z.array(z.object({ seq: z.int().positive(), at: EpochMs, slash: z.boolean(), background: z.boolean().optional(), chars: Count, requests: Count, actions: Count, errors: Count, durationMs: z.number().nonnegative(), steps: z.array(TimelineStep) })),
  totals: z.object({ waitMs: ValuedNumber, toolMs: ValuedNumber, openMs: z.number().nonnegative(), failedCalls: Count }),
});
export type RecordTimelineResponse = z.infer<typeof RecordTimelineResponse>;

/**
 * GET /api/v1/records/:ref/content — question/answer text. Own record: always (by level). Others: only with an active
 * per-conversation grant; served with `Cache-Control: no-store`. Text keeps the stored canonical redaction markers
 * ([EMAIL_<token>], [REDACTED:<kind>], etc.); the web also understands legacy `{{redact:<kind>}}` markers.
 */
export const RecordContentResponse = z.object({
  watermark: Watermark.nullable(),
  prompts: z.array(z.object({ seq: z.int().positive(), at: EpochMs, slash: z.boolean(), text: z.string().nullable(), chars: Count })),
  /** `agent`: a subagent's answer inside the turn (Claude, D-445), shown apart from the main answer. */
  answers: z.array(z.object({ seq: z.int().positive(), at: EpochMs, text: z.string().nullable(), refused: z.boolean(), agent: AgentRef.nullable().optional() })),
  promptsState: TextState,
  answersState: TextState,
});
export type RecordContentResponse = z.infer<typeof RecordContentResponse>;

/** Search is bound to one readable conversation; the query is sent in the body, never a URL. */
export const RecordSearchRequest = z.object({ q: z.string().trim().min(1).max(100) }).strict();
export const RecordSearchResponse = z.object({
  watermark: Watermark.nullable(), total: Count, truncated: z.boolean(),
  matches: z.array(z.object({ kind: z.enum(['prompt', 'answer']), seq: z.int().positive(), at: EpochMs, excerpt: z.string() })).max(100),
});
export type RecordSearchResponse = z.infer<typeof RecordSearchResponse>;

/** Technical detail (logs.raw): server-masked OTel-shaped JSON; text fields outside permission are removed. */
export const RecordRawResponse = z.object({
  resource: z.record(z.string(), z.unknown()),
  receivedAt: EpochMs,
  channel: Channel,
  via: Via,
  events: z.array(z.object({ name: z.string(), timestamp: EpochMs, resource: z.record(z.string(), z.unknown()).optional(), attributes: z.record(z.string(), z.unknown()) })),
  removedFields: z.array(z.string()),
});

/** POST /api/v1/records/:ref/access — fixed content-view flow step ②④ (needs a valid step-up re-auth). */
export const AccessGrantRequest = z.object({
  reasonCategory: ReasonCategory.optional(),
  /** Trimmed; ≥ policy minimum (10/15/20/30). Too short → 422 `reason_too_short` and a denied audit row. */
  reason: z.string().trim().min(1).max(200).optional(),
  /** Inside an approved investigation case the case replaces the reason (re-auth & grant still apply). */
  caseId: Id.optional(),
}).superRefine((value, ctx) => {
  if (!value.caseId && !value.reasonCategory) ctx.addIssue({ code: 'custom', path: ['reasonCategory'], message: 'A reason category is required outside an investigation' });
  if (!value.caseId && !value.reason) ctx.addIssue({ code: 'custom', path: ['reason'], message: 'A reason is required outside an investigation' });
});
export const AccessGrantResponse = z.object({ grantId: Id, grantedAt: EpochMs, expiresAt: EpochMs, minutes: z.int().min(10).max(60), watermark: Watermark });
export const AccessStatusResponse = z.object({
  active: z.boolean(),
  expiresAt: EpochMs.nullable(),
  reauthUntil: EpochMs.nullable(),
  lastDeniedAt: EpochMs.nullable(),
  policy: z.object({ minReasonLength: z.int(), grantMinutes: z.int(), reauthValidMinutes: z.int() }),
  cases: z.array(z.object({ id: Id, code: z.string(), title: z.string() })).default([]),
});

/** Previous/next record in the list order — with what the menu prints (own title, else "대화 #no · 질문 n개 · n자"). */
export const NeighborRecord = z.object({ record: RecordRef, startedAt: EpochMs, title: z.string().nullable(), prompts: Count, chars: Count });
export const RecordNeighborsResponse = z.object({ prev: NeighborRecord.nullable(), next: NeighborRecord.nullable() });

/** POST /api/v1/records/export — rows (pseudonymous, no text) by default; identity/content kinds need reasons. */
export const RecordExportRequest = ExportRequest.and(
  z.object({ list: z.object({ tool: z.array(Tool).optional(), model: z.array(z.string()).optional(), surface: SurfaceFilter.optional(), q: z.string().max(100).optional() }).optional() }),
);

/**
 * P3 list views (plan 8.4.3 U-B14): "낱개 기록" (one row per normalized event, plain name + original event name) and
 * "파일·명령" (one row per file action / command). Same filter and permission rules as the conversation list;
 * full paths / command text only for own records or under an active grant.
 */
export const RecordEventsQuery = filterQuery({
  tool: ToolFilter.optional(),
  model: ModelFilter.optional(),
  surface: SurfaceFilter.optional(),
  q: z.string().trim().max(100).optional(),
  kind: z.string().regex(/^[a-z.]+(,[a-z.]+)*$/).max(300).optional(),
  ...PageQuery.shape,
});
export const RecordEventsResponse = z.object({
  applied: AppliedFilter,
  items: z.array(
    z.object({
      at: EpochMs,
      record: RecordRef,
      person: PersonRef,
      tool: Tool,
      kind: z.string(),
      label: EventLabel,
      rawName: z.string(),
      detail: z.string().nullable(),
      detailState: z.object({ state: DisplayState, reason: MissingReason.optional() }).nullable(),
    }),
  ),
  page: PageMeta,
  footer: CardFooter,
});
export const RecordWorkQuery = filterQuery({
  tool: ToolFilter.optional(),
  type: z.enum(['file', 'command']).optional(),
  q: z.string().trim().max(100).optional(),
  ...PageQuery.shape,
});
export const RecordWorkResponse = z.object({
  applied: AppliedFilter,
  items: z.array(
    z.object({
      at: EpochMs,
      record: RecordRef,
      person: PersonRef,
      tool: Tool,
      type: z.enum(['file', 'command']),
      /** File label (last two segments) or program name. */
      label: z.string(),
      /** Full path / command text — own record or active grant only; else null with `fullState`. */
      full: z.string().nullable(),
      fullState: z.object({ state: DisplayState, reason: MissingReason.optional() }).nullable(),
      action: FileKind.nullable(),
      exitCode: z.int().nullable(),
      estimated: z.boolean(),
    }),
  ),
  page: PageMeta,
  footer: CardFooter,
});

/** Recent conversations card (home): same title/project rule as the list. */
export const RecentConversation = RecordRow.pick({ record: true, startedAt: true, person: true, tool: true, title: true, project: true, prompts: true, chars: true, tokens: true, recordLevel: true }).extend({
  activeMs: ValuedNumber,
});
export type RecentConversation = z.infer<typeof RecentConversation>;
/** Recent conversations on someone's person page: no AI active time (never shown per person). */
export const PersonRecentRow = RecentConversation.omit({ activeMs: true });
export type PersonRecentRow = z.infer<typeof PersonRecentRow>;

export const recordsSchemas = {
  RecordRefParams,
  RecordListQuery,
  RecordListResponse,
  RecordDeletedDetails,
  RecordDetailResponse,
  RecordUsageResponse,
  RecordActionsResponse,
  RecordFilesResponse,
  RecordTimelineResponse,
  RecordContentResponse,
  RecordSearchRequest,
  RecordSearchResponse,
  RecordRawResponse,
  AccessGrantRequest,
  AccessGrantResponse,
  AccessStatusResponse,
  RecordNeighborsResponse,
  RecordExportRequest,
  RecordEventsQuery,
  RecordEventsResponse,
  RecordWorkQuery,
  RecordWorkResponse,
} as const;

export type RecordRawResponse = z.infer<typeof RecordRawResponse>;

export type AccessGrantResponse = z.infer<typeof AccessGrantResponse>;

export type AccessStatusResponse = z.infer<typeof AccessStatusResponse>;

export type RecordNeighborsResponse = z.infer<typeof RecordNeighborsResponse>;

export type RecordEventsResponse = z.infer<typeof RecordEventsResponse>;

export type RecordWorkResponse = z.infer<typeof RecordWorkResponse>;
